<template>
	<div class="warehouse-menu-editor-container">
		<ModalHeader :title="getString('menu_products', [userStore.menuWord])" />
		<div class="menu-picker">
			<div class="multi-menu filter-row">
				<MenusFilter ref="menus-filter" :menus="sortedMenus" @select-menu="(menuId) => activeMenu = menuId" />
			</div>
			<div class="categories filter-row">
				<CategoriesFilter ref="categories-filter" :selected-categories="filterableCategories" @select-category="(catId) => activeCategory = catId" />
			</div>
			<div class="items">
				<div class="item-row row space-between align-center" v-for="item in itemsToShow" :key="item.menu_item_id">
					<div class="item-name">{{ item.name }}</div>
					<div class="item-add">
						<div class="item-sku" v-if="!!item.sku">{{ item.sku }}</div>
						<div class="item-new-sku" v-else>
							<AppTextInput :ref="'item-new-sku-'+item.menu_item_id" :placeholder="getString('sku')" />
						</div>
						<AppButton text="+" outline @click="addItem(item)" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import CategoriesFilter from "@/console-new/components/ops/counter/CategoriesFilter.vue";
import {useMenuStore, useUserStore} from "@/console-new/stores";
import MenusFilter from "@/console-new/components/ops/counter/MenusFilter.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import API from "@/shared/helpers/API";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const menuStore = useMenuStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		return {
			getString: languageStore.getString,
			menuStore,
			userStore,
			notificationStore,
		}
	},
	name: "WarehouseMenuEditor",
	components: {AppTextInput, AppButton, CategoriesFilter, ModalHeader, MenusFilter},
	emits: ["changed-products"],
	data() {
		return {
			activeMenu: null,
			activeCategory: null,
		}
	},
	computed: {
		selectedMenu() {
			return this.activeMenu !== null ? this.menuStore.menus[this.activeMenu] : null;
		},
		sortedMenus() {
			return this.menuStore.sortedMenus;
		},
		filterableCategories() {
			if(this.selectedMenu && this.selectedMenu.categories) {
				let categories = Object.values(this.selectedMenu.categories);
				return categories.sort((a, b) => a.order_number - b.order_number).filter(c => Object.keys(c.items).length > 0);
			} else {
				return [];
			}
		},
		itemsToShow() {
			if(this.selectedMenu && this.selectedMenu.categories && this.activeCategory !== null && this.selectedMenu.categories.hasOwnProperty(this.activeCategory)) {
				let category = this.selectedMenu.categories[this.activeCategory];
				return Object.values(category.items).sort((a, b) => a.order_number - b.order_number).filter(i => !i.deleted_item && !i.warehouse);
			} else {
				return [];
			}
		},
	},
    methods: {
        addItem(item) {
            let sku = item.sku;
            if (!sku) {
                sku = this.$refs["item-new-sku-" + item.menu_item_id][0].currentValue;
            }
            if(sku) {
                API.init().addWarehouseItem(this.userStore.currBusinessId, item.menu_item_id, sku,(r) => {
                    if(r && r.result && r.result === "OK") {
						item.warehouse = true;
						item.sku = sku;

						this.$emit("changed-products");
						this.notificationStore.showNotification("generic-success");

						if(this.menuStore.items && this.menuStore.items.hasOwnProperty(item.menu_item_id)) {
							this.menuStore.items[item.menu_item_id].warehouse = true;
							this.menuStore.items[item.menu_item_id].sku = sku;
							if(this.menuStore.modMenu && this.menuStore.modMenu.menu_id) {
								this.menuStore.changeModMenu(this.menuStore.modMenu.menu_id);
							}
						}
                    } else {
						this.notificationStore.showNotification("generic-fail");
					}
                });
            }
        }
    },
	created() {
		if (!this.menuStore.loadedBusinessMenu && this.menuStore.currBusinessId !== null) {
			this.menuStore.loadMenu();
		}
	},
	mounted() {
		let lastMenu = localStorage.getItem("last_menu_id");
		if (lastMenu !== null) {
			this.activeMenu = Number(lastMenu);
		}
		this.$refs["menus-filter"].activeMenu = this.activeMenu;
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.menuStore.loadMenu();
			}
		},
		filterableCategories: function (newVal, oldVal) {
			if (newVal.length > 0 && newVal !== oldVal) {
				this.activeCategory = newVal[0].menu_category_id;
				this.$refs["categories-filter"].activeCategory = this.activeCategory;
			}
		},
	}
}
</script>

<style scoped lang="scss">
.menu-picker {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	width: 100%;
	position: relative;

	.filter-row {
		width: 100%;
		display: flex;
		flex-direction: row;
		align-items: center;
		overflow-x: auto;
		@include hideScrollbar;
		border-radius: 13px;
		box-shadow: inset 2px 2px 3px #0752A34D;
	}

    .multi-menu {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        background-color: var(--color-bg-primary-1);
    }

    .categories {
        margin-top: 0;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        background-color: var(--color-bg-primary-2);
    }

	.items {
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		align-items: flex-start;
		margin-top: 15px;

		.item-row {
			width: 100%;
			border-bottom: 1px solid $primary;
			padding: 8px 0;

			.item-add {
				display: flex;
				flex-direction: row;
				justify-content: flex-end;
				align-items: center;
				gap: 12px;

				.item-new-sku {
					.input-wrapper {
						width: 150px;
					}
				}
			}

			.base-button {
				padding: 7px 12px;
				min-width: 0;
			}

			&:last-child {
				border-bottom: none;
			}
		}
	}
}
</style>